'use server'

import { apiRequest, ApiResponse } from '../request'

export type GoogleAdsConnectionStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'REVOKED'

export interface GoogleAdsConnectionSummary {
    connected: boolean
    status: GoogleAdsConnectionStatus | null
    googleEmail: string | null
    customerId: string | null
    customerName: string | null
    loginCustomerId: string | null
    scopes: string[]
    lastError: string | null
}

export interface GoogleAdsCustomer {
    id: string
    name: string | null
    isManager: boolean
}

export interface GoogleAdsCustomersResult {
    ready: boolean
    /** O Google ainda não liberou o acesso (resolve sozinho); falso = o usuário precisa agir. */
    awaitingApproval?: boolean
    reason?: string
    customers: GoogleAdsCustomer[]
}

/** URL de autorização do Google (o front faz window.location.href). */
export async function getGoogleAdsOAuthUrl(): Promise<ApiResponse<{ url: string }>> {
    return apiRequest<{ url: string }>('marketing/google-ads/oauth/url', {})
}

export async function getGoogleAdsConnection(): Promise<ApiResponse<GoogleAdsConnectionSummary>> {
    return apiRequest<GoogleAdsConnectionSummary>('marketing/google-ads/connection', {})
}

export async function getGoogleAdsCustomers(): Promise<ApiResponse<GoogleAdsCustomersResult>> {
    return apiRequest<GoogleAdsCustomersResult>('marketing/google-ads/connection/customers', {})
}

export async function updateGoogleAdsSelection(data: {
    customerId?: string | null
    loginCustomerId?: string | null
}): Promise<ApiResponse<GoogleAdsConnectionSummary>> {
    return apiRequest<GoogleAdsConnectionSummary>('marketing/google-ads/connection', { data }, 'patch')
}

export async function disconnectGoogleAds(): Promise<ApiResponse<{ disconnected: boolean }>> {
    return apiRequest<{ disconnected: boolean }>('marketing/google-ads/connection', {}, 'delete')
}

/** Webhook do formulário de lead do Google Ads: independe da conexão por OAuth. */
export interface GoogleAdsLeadFormSummary {
    webhookUrl: string
    key: string | null
    lastReceivedAt: string | null
    lastWasTest: boolean
}

export async function getGoogleAdsLeadForm(): Promise<ApiResponse<GoogleAdsLeadFormSummary>> {
    return apiRequest<GoogleAdsLeadFormSummary>('marketing/google-ads/lead-form', {})
}

/** Gera a chave ou troca a atual (a antiga deixa de ser aceita). */
export async function rotateGoogleAdsLeadFormKey(): Promise<ApiResponse<GoogleAdsLeadFormSummary>> {
    return apiRequest<GoogleAdsLeadFormSummary>('marketing/google-ads/lead-form/key', {}, 'post')
}

/** Google Analytics 4 e tag do Google Ads nas lojas online (`StoreGoogleTag`). */
export interface StoreGoogleTagItem {
    id: number
    name: string | null
    measurementId: string | null
    adsTagId: string | null
    adsPurchaseLabel: string | null
    adsensePublisherId: string | null
    /** Vazio: vale para todas as lojas da organização. */
    stores: { storeConfigId: number }[]
}

export interface StoreGoogleTagInput {
    name?: string | null
    measurementId?: string | null
    adsTagId?: string | null
    adsPurchaseLabel?: string | null
    adsensePublisherId?: string | null
    storeConfigIds?: number[]
}

export interface StoreGoogleTagStore {
    id: number
    name: string
    subdomain: string
    customDomain: string | null
    logo: string | null
}

export async function listStoreGoogleTags(): Promise<ApiResponse<StoreGoogleTagItem[]>> {
    return apiRequest<StoreGoogleTagItem[]>('marketing/google-ads/store-tags', {})
}

export async function listStoreGoogleTagStores(): Promise<ApiResponse<StoreGoogleTagStore[]>> {
    return apiRequest<StoreGoogleTagStore[]>('marketing/google-ads/store-tags/stores', {})
}

export async function createStoreGoogleTag(data: StoreGoogleTagInput): Promise<ApiResponse<StoreGoogleTagItem>> {
    return apiRequest<StoreGoogleTagItem>('marketing/google-ads/store-tags', { data }, 'post')
}

export async function updateStoreGoogleTag(
    id: number,
    data: StoreGoogleTagInput,
): Promise<ApiResponse<StoreGoogleTagItem>> {
    return apiRequest<StoreGoogleTagItem>(`marketing/google-ads/store-tags/${id}`, { data }, 'patch')
}

export async function removeStoreGoogleTag(id: number): Promise<ApiResponse<{ removed: boolean }>> {
    return apiRequest<{ removed: boolean }>(`marketing/google-ads/store-tags/${id}`, {}, 'delete')
}
